import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import RenderReactPdfViewer from '@/components/react-pdf-viewer/RenderReactPdfViewer'
import HTMLBaseRenderer from '@/content-viewer/components/base-renderers/HTMLBaseRenderer'
import ErrorBoundary from '@/components/error/ErrorBoundary'
import ContentViewerDeletedFileState from '@/content-viewer/components/common/ContentViewerDeletedFileState'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import { RenderFormat } from '@/content-viewer/store/schemas'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import ReferenceHtmlRenderer from '@/reference-highlighting/components/ReferenceHtmlRenderer'
import { useTrackHighlightMatchStatus } from '@/reference-highlighting/hooks/useTrackHighlightMatchStatus'
import { kUserFileUnsupportedPreviewMessage } from '@/user_files/constants'
import { MimeType } from '@/constants/constants-mime-types'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useUserFileViewableContent } from '../hooks/useUserFileViewableContent'
import { useUserFiles } from '../hooks/useUserFiles'

// SuperDoc is large, so it loads only when a Word file opens.
const DocxReferenceRenderer = lazy(() => import('./DocxReferenceRenderer'))

type UserFileContentRendererProps = {
  userFileId: string
  // Omit format to resolve PDF vs markdown from the viewable-url response at load time.
  format?: RenderFormat.PDF | RenderFormat.MARKDOWN
  title?: string
  pdfHighlights: PaxtonHighlightArea[]
  markdownHighlights: string[]
  startingPage?: number
  pdfExcerpt?: string
}

export default function UserFileContentRenderer(props: UserFileContentRendererProps): JSX.Element {
  const { userFileId, format, pdfHighlights, markdownHighlights } = props
  const { enabled: citationHighlightsImprovements, loading: flagLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.CITATION_HIGHLIGHTS_IMPROVEMENTS)
  const [unrenderableFileId, setUnrenderableFileId] = useState<string | null>(null)
  // The file's type as the upload pipeline detected it from the file's contents, not its name.
  const { filesById, unavailableIds, error: recordError } = useUserFiles([userFileId])
  const record = filesById[userFileId]
  const fileTypeLoading = !record && !recordError && !unavailableIds.includes(userFileId)
  const fileType = record?.detected_content_type ?? null
  // With the flag on, a Word file opens in its original layout instead of its converted text. The Word
  // viewer paints text passages only, so a file opened with page-geometry highlights keeps its old view,
  // and a file SuperDoc cannot render falls back to its converted text.
  const mayOpenInWordViewer = !flagLoading && citationHighlightsImprovements && pdfHighlights.length === 0 && unrenderableFileId !== userFileId
  if (mayOpenInWordViewer && fileTypeLoading) return <ContentViewerLoadingSpinner size="small" />
  if (mayOpenInWordViewer && record && fileType === MimeType.DOCX) {
    // SuperDoc's files are left out of the offline cache, so a tab still running an older release can fail
    // to load them after a deploy; the file then shows as converted text, and the app's update prompt offers
    // the new release. Keyed by file so one file's late render failure can never be read as the next file's.
    const docxRenderer = (
      <ErrorBoundary key={userFileId} fallback={() => <ShowConvertedText onShow={() => setUnrenderableFileId(userFileId)} />}>
        <Suspense fallback={<ContentViewerLoadingSpinner size="small" />}>
          <DocxReferenceRenderer
            userFileId={userFileId}
            fileName={record.original_filename}
            passages={markdownHighlights}
            onRenderFailed={() => setUnrenderableFileId(userFileId)}
          />
        </Suspense>
      </ErrorBoundary>
    )
    return format === undefined ? <div className="flex-1 w-full h-full overflow-y-auto pb-4">{docxRenderer}</div> : docxRenderer
  }
  return <UserFileViewableContentRenderer {...props} fileType={fileType} fileTypeLoading={fileTypeLoading} />
}

/** Hand a file back to its converted text from an error boundary's fallback, which renders during an error. */
function ShowConvertedText({ onShow }: { onShow: () => void }): null {
  useEffect(() => onShow(), [onShow])
  return null
}

function UserFileViewableContentRenderer(props: UserFileContentRendererProps & { fileType: string | null; fileTypeLoading: boolean }): JSX.Element {
  const { userFileId, format, title, pdfHighlights, markdownHighlights, startingPage, pdfExcerpt, fileType, fileTypeLoading } = props
  const { enabled: citationHighlightsImprovements, loading: flagLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.CITATION_HIGHLIGHTS_IMPROVEMENTS)
  const { loading, error, deleted, pdfFile, htmlString, retry } = useUserFileViewableContent({
    userFileId,
    format,
    title,
    citationHighlightsImprovements,
  })
  const markdownContainerRef = useRef<HTMLDivElement>(null)
  useTrackHighlightMatchStatus({
    containerRef: markdownContainerRef,
    contentId: userFileId,
    htmlContent: loading ? null : htmlString,
    passages: markdownHighlights,
    citationHighlightsImprovements,
    flagLoading,
    fileType,
    fileTypeLoading,
  })

  const errorMessage =
    format === RenderFormat.PDF
      ? 'Failed to load PDF file.'
      : format === RenderFormat.MARKDOWN
      ? 'Failed to load markdown content.'
      : 'Failed to load file content.'

  if (loading || flagLoading) {
    return <ContentViewerLoadingSpinner size="small" />
  }

  // A deleted file is gone for good, so this state carries no Retry.
  if (deleted) {
    return <ContentViewerDeletedFileState />
  }

  // No Retry here either: the file has no viewable representation, so retrying cannot succeed.
  if (error === 'unsupported') {
    return <ContentViewerErrorState message={kUserFileUnsupportedPreviewMessage} />
  }

  if (error) {
    return <ContentViewerErrorState message={errorMessage} onRetry={retry} />
  }

  if (pdfFile) {
    return <RenderReactPdfViewer pdfFile={pdfFile} highlightAreas={pdfHighlights} startingPage={startingPage} excerpt={pdfExcerpt} />
  }

  if (htmlString) {
    const htmlRenderer = (
      // `contents` keeps the wrapper out of layout; it only gives the match-status report a root to read.
      <div ref={markdownContainerRef} className="contents">
        {citationHighlightsImprovements ? (
          <ReferenceHtmlRenderer htmlContent={htmlString} passages={markdownHighlights} />
        ) : (
          <HTMLBaseRenderer htmlContent={htmlString} highlightSentences={markdownHighlights} />
        )}
      </div>
    )
    // A declared markdown format gets its scroll wrapper from the hosting view; when the
    // format was resolved at load time the host could not know to add one, so add it here.
    return format === undefined ? <div className="flex-1 w-full h-full overflow-y-auto pb-4">{htmlRenderer}</div> : htmlRenderer
  }

  return <ContentViewerErrorState message={errorMessage} onRetry={retry} />
}
